// @flow
import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import Toggle from '@atlaskit/toggle';
import Page, { Grid, GridColumn } from '@atlaskit/page';
import SectionMessage from '@atlaskit/section-message';
import PageHeader from '@atlaskit/page-header';
import { compose, graphql, withApollo } from 'react-apollo';
import { gridSize } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tick from '@atlaskit/icon/glyph/check-circle';
import useFlagContext from '../hooks/useFlagContext';
import Loader from '../components/utils/Loader/Loader';
import { PROJECT_ENABLED_UPDATE } from '../graphql/mutations';
import { PROJECT_IS_ENABLED } from '../graphql/queries';
import { ErrorParser } from '../utils/ErrorParser';
import NoPermission from '../components/no-permission/NoPermission';
import { withFlagContext } from '../contexts/flagContext';

const Container = styled.div`
    color: #172b4d;
    margin: 0;
    padding: 0;
    height: 100vh;
`;

const Wrap = styled.div`
    padding-left: ${5 * gridSize()}px;
`;

const HeaderText = styled.div`
    font-weight: 700;
    font-size: 16px;
    margin-bottom: 10px;
    color: ${token('color.text')};
`;

const WrapSettings = styled.div`
    align-items: flex-start;
    display: flex;
    flex-wrap: wrap;
    margin: 0px auto;
    max-width: ${(props) => (props.fullWidth ? '100%' : '960px')};
    padding: 0px 20px;
    position: relative;
    width: ${(props) => (props.fullWidth ? '100%' : 'auto')};
`;

const Description = styled.div`
    font-size: 14px;
    font-weight: 400;
    line-height: 1.42857143;
    letter-spacing: 0;
    color: ${token('color.text')};
`;

const WrapToggle = styled.div``;

const OctoPageSettings = (props) => {
    const { hideHeader = false, fullWidth = false } = props;
    const [isChecked, setIsChecked] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [projectId, setProjectId] = useState('');
    const [error, setError] = useState('');
    const [permissionError, setPermissionError] = useState(false);
    const { pushFlag } = useFlagContext();
    const text = `When disabled Octo Component Management app won't appear in the project menu and in issues`;

    const getProjectEnabledProperty = useCallback(
        async (aProjectId) => {
            if (aProjectId) {
                try {
                    const statusEnabled = await props.client.query({
                        query: PROJECT_IS_ENABLED,
                        variables: { projectId: aProjectId },
                    });

                    if (statusEnabled.data?.projectIsEnabled) {
                        setIsChecked(statusEnabled.data.projectIsEnabled.status);
                    }
                } catch (err) {
                    if (ErrorParser.parse(err.message) === "You don't have permission to do this action") {
                        setPermissionError(true);
                        setError('');
                    } else {
                        setError(err.message || 'Please try again later');
                        setPermissionError(false);
                    }
                } finally {
                    setIsLoading(false);
                }
            }
        },
        [projectId],
    );

    const setProjectEnabledProperty = useCallback(
        async (aProjectId, isEnabled) => {
            if (aProjectId) {
                props
                    .updateEnableProject({
                        variables: {
                            projectId: aProjectId,
                            isEnabled,
                        },
                    })
                    .then(() => {
                        pushFlag({
                            created: Date.now(),
                            icon: <Tick primaryColor="#00875A" />,
                            title: `Octo Component Management app in this project ${
                                isEnabled ? 'enabled' : 'disabled'
                            } successfully.`,
                            key: 'success',
                        });
                    })
                    .catch((err) => {
                        if (ErrorParser.parse(err.message) === "You don't have permission to do this action") {
                            setPermissionError(true);
                            setError('');
                        } else {
                            setError(err.message || 'Please try again later');
                            setPermissionError(false);
                        }
                    });
            }
        },
        [projectId, isChecked],
    );

    useEffect(() => {
        window.PlatformAdapter.resizeWindow('100%', '800px');
        window.PlatformAdapter.getJiraContext().then((context) => {
            const jiraProjectId = parseInt(context?.jira?.project?.id || 0, 10);
            if (jiraProjectId > 0) {
                setProjectId(jiraProjectId);
                getProjectEnabledProperty(jiraProjectId);
            } else {
                setError(true);
            }
        });
    }, [getProjectEnabledProperty]);

    const projectToggle = () => {
        setIsChecked(!isChecked);
        setProjectEnabledProperty(projectId, !isChecked);
    };

    if (permissionError) {
        return (
            <Container centered>
                <NoPermission />
            </Container>
        );
    }

    if (isLoading) {
        return (
            <Container centered>
                <Loader size="large" withFullWidth={true} />
            </Container>
        );
    }

    return (
        <Container>
            <Page>
                {error && (
                    <SectionMessage appearance="error">
                        <p>There was an error when trying to enable/disable the Octo:</p>
                        <p style={{ whiteSpace: 'pre-wrap' }}>{ErrorParser.parse(error)}</p>
                    </SectionMessage>
                )}
                {!hideHeader && (
                    <Wrap>
                        <PageHeader>Octo Component Management</PageHeader>
                    </Wrap>
                )}
                <WrapSettings fullWidth={fullWidth}>
                    <Grid spacing="comfortable">
                        <GridColumn medium={8}>
                            <HeaderText>Enable/Disable Octo Component Management app in this project</HeaderText>
                            <Description>{text}</Description>
                        </GridColumn>
                        <GridColumn medium={3}>
                            <WrapToggle>
                                <Toggle
                                    id="octo-enabled"
                                    size="large"
                                    isChecked={isChecked}
                                    onChange={() => projectToggle()}
                                />
                            </WrapToggle>
                        </GridColumn>
                    </Grid>
                </WrapSettings>
            </Page>
        </Container>
    );
};

export default compose(
    withFlagContext,
    graphql(PROJECT_ENABLED_UPDATE, { name: 'updateEnableProject' }),
    withApollo,
)(OctoPageSettings);
